/* eslint-disable max-lines */
import { Browser } from "@capacitor/browser";
import { Checkbox, SystemInlineBanner, Text } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, FormControl, FormControlLabel, Link, Stack } from "@mui/material";
import { NoAgreementTemplateFileAvailable } from "@src/appV2/Agreements/NoAgreementTemplateFileAvailable";
import { type Agreement, type SignedAgreement } from "@src/appV2/Agreements/types";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { usePdfViewerContext } from "@src/appV2/PdfViewer/context";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { useSelectedWorkType } from "@src/appV2/redesign/Onboarding/hooks/useSelectedWorkType";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { SupportArticles, SupportContext } from "@src/appV2/support/constants";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { type Worker } from "@src/appV2/Worker/api/types";
import { USER_EVENTS } from "@src/constants";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { OnboardingTitle } from "../../SharedTitle";
import { ONBOARDING_STAGES } from "../../util/constants";
import { useSignICAgreement } from "./useSignICAgreement";

export interface AgreementsProps {
  onAcceptAgreement: () => Promise<void>;
  worker: Worker;
  agreementsStatus: "loading" | "error" | "success";
  agreementToSign: Agreement | SignedAgreement;
}

const signAgreementFormSchema = z.object({
  confirmation: z.boolean().refine((value) => value, {
    message: "Click the checkbox to confirm and proceed.",
  }),
  agreementScrolled: z.boolean().refine((value) => value, {
    message: "You must read the full agreement by scrolling to the bottom to proceed.",
  }),
});

export function ICAgreementForm(props: AgreementsProps) {
  const { agreementsStatus, agreementToSign, worker, onAcceptAgreement } = props;
  const { isFlexAndPermanentPlacementsOnboarding } = useSelectedWorkType();
  const { mutateAsync: signICAgreement, isLoading: isSigning } = useSignICAgreement({
    onSuccess: async () => {
      logEvent(USER_EVENTS.SIGNED_CONTRACT, {
        // Keep attributes same as in the old event to avoid breaking any old analytics code
        hcpID: worker.userId,
        email: worker.email,
        signature: worker.name,
        agreementVersion: agreementToSign?.attributes.version,
      });
      logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
        stepCompleted: ONBOARDING_STAGES.CONTRACTOR_AGREEMENT,
        signature: worker.name,
        agreementVersion: agreementToSign?.attributes.version,
      });
      await onAcceptAgreement();
    },
    onError: (error) => {
      logError(APP_V2_APP_EVENTS.SIGN_AGREEMENT_FAILURE, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          workerName: worker.name,
          agreementVersion: agreementToSign?.attributes.version,
        },
      });
    },
  });

  const { showErrorToast } = useToast();

  const [showConfirmationErrorBanner, setShowConfirmationErrorBanner] = useState(false);
  const [showAgreementScrolledErrorBanner, setShowAgreementScrolledErrorBanner] = useState(false);

  const formMethods = useForm({
    defaultValues: {
      confirmation: false,
      agreementScrolled: false,
    },
    mode: "all",
    resolver: zodResolver(signAgreementFormSchema),
  });

  const {
    formState: { errors, isValid: formIsValid },
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
  } = formMethods;

  const { howToContactSupportLink } = useSupportLinks();
  const { currentPage, numberOfPages, isSupportedPlatform } = usePdfViewerContext();

  useLogEffect(
    APP_V2_USER_EVENTS.SIGN_AGREEMENT_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
    },
    { enabled: isDefined(agreementToSign) }
  );

  useEffect(() => {
    void trigger();
  }, [trigger]);

  useEffect(() => {
    if (currentPage === numberOfPages || isSupportedPlatform === false) {
      setValue("agreementScrolled", true, { shouldValidate: true });
      setShowAgreementScrolledErrorBanner(false);
    }
  }, [currentPage, numberOfPages, isSupportedPlatform, setValue]);

  useLogEffect(APP_V2_APP_EVENTS.PDF_PLATFORM_UNSUPPORTED_ONBOARDING, {
    enabled: isSupportedPlatform === false,
  });

  const handleContactSupport = async () => {
    try {
      await Browser.open({
        url: howToContactSupportLink,
        windowName: "_blank",
        presentationStyle: "popover",
      });
    } catch (error) {
      logError(APP_V2_USER_EVENTS.OPEN_AGREEMENT_SUPPORT_ARTICLE_ERROR, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          errorMessage: toError(error).message,
        },
      });

      // This should rarely happen, but we want to show a toast to the user if it does.
      showErrorToast(
        `Error opening agreement support article. Please manually go to the link: ${howToContactSupportLink}`
      );
      return;
    }

    logEvent(APP_V2_USER_EVENTS.OPENED_SUPPORT_ARTICLE, {
      articleLink: howToContactSupportLink,
      articleName: SupportArticles.HOW_TO_CONTACT_SUPPORT,
      userId: worker.userId,
      supportContext: SupportContext.IC_AGREEMENT_ERROR_ALERT,
    });
  };

  return (
    <>
      <PageContent>
        <OnboardingTitle text="Independent Contractor Agreement" />

        {showAgreementScrolledErrorBanner && (
          <SystemInlineBanner color="error" iconType="triangle-exclamation" sx={{ marginTop: 6 }}>
            <Text variant="body2" color="inherit">
              You must scroll to the bottom of the page before signing. Having trouble?&nbsp;
              <Link
                variant="body2"
                sx={{
                  color: "inherit",
                  textDecorationColor: "inherit",
                  ":hover": {
                    color: "inherit",
                    textDecoration: "underline",
                  },
                }}
                onClick={handleContactSupport}
              >
                Contact our support team
              </Link>
              .
            </Text>
          </SystemInlineBanner>
        )}

        {showConfirmationErrorBanner && (
          <SystemInlineBanner color="error" iconType="triangle-exclamation" sx={{ marginTop: 6 }}>
            <Text variant="body2" color="inherit">
              You must click the checkbox to confirm and proceed. Having trouble?&nbsp;
              <Link
                variant="body2"
                sx={{
                  color: "inherit",
                  textDecorationColor: "inherit",
                  ":hover": {
                    color: "inherit",
                    textDecoration: "underline",
                  },
                }}
                onClick={handleContactSupport}
              >
                Contact our support team
              </Link>
              .
            </Text>
          </SystemInlineBanner>
        )}

        <Box sx={{ flex: "1 1 auto", overflow: "auto", marginTop: 6 }}>
          {agreementsStatus === "loading" && <PdfViewerLoadingSkeleton />}
          {agreementsStatus === "error" && <NoAgreementTemplateFileAvailable />}
          {agreementsStatus === "success" && <PdfViewer />}
        </Box>
        <FormControl component="fieldset" variant="standard">
          <input type="hidden" {...register("agreementScrolled")} />
        </FormControl>
      </PageContent>
      <PageFooterWrapper>
        <Stack spacing={4}>
          {(Boolean(errors.agreementScrolled) || Boolean(errors.confirmation)) && (
            <Text variant="body2" color="error">
              {errors.agreementScrolled?.message} {errors.confirmation?.message}
            </Text>
          )}
          <Controller
            name="confirmation"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    sx={{ alignSelf: "flex-start", marginRight: 2 }}
                    onChange={(event) => {
                      field.onChange(event.target.checked);
                      setShowConfirmationErrorBanner(false);
                    }}
                  />
                }
                label={
                  <Stack spacing={2}>
                    <Text variant="body2">
                      By checking this box, you are signing this agreement electronically and
                      agreeing to have your electronic signature in the name of <b>{worker.name}</b>{" "}
                      added to the agreement.
                    </Text>

                    {isFlexAndPermanentPlacementsOnboarding && (
                      <Text variant="body2">
                        These terms only apply to picking up shifts as an independent contractor. If
                        you are interviewing for jobs, you will be employed and paid by the
                        facility.
                      </Text>
                    )}
                  </Stack>
                }
              />
            )}
          />
          <LoadingButton
            fullWidth
            size="large"
            variant={formIsValid ? "contained" : "outlined"}
            disabled={agreementsStatus === "loading"}
            isLoading={agreementsStatus === "loading" || isSigning}
            sx={{ lineHeight: "1.5rem" }}
            onClick={handleSubmit(
              async () => {
                await signICAgreement({
                  workerId: worker.userId,
                  agreementVersion: agreementToSign.attributes.version,
                  signature: worker.name,
                });
              },
              (errors) => {
                if (errors.agreementScrolled) {
                  setShowAgreementScrolledErrorBanner(true);
                } else if (errors.confirmation) {
                  setShowConfirmationErrorBanner(true);
                }
              }
            )}
          >
            I have read and agree to this Independent Contractor Agreement
          </LoadingButton>
        </Stack>
      </PageFooterWrapper>
    </>
  );
}

/* eslint-enable max-lines */
